import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import moment from 'moment';
import { message } from '@bb/elements-web';
import {
  getKeyholderBillingProfile,
  updateKeyholderBillingProfile,
} from 'api/SupraController';
import { AuthContext } from 'contexts';
import type {
  IBillingProfileDataWeb,
  IBillingProfileData,
  IKeyholderDataWeb,
  IKeyholderProfileData,
} from '@/app-package';
import {
  getBillingProfile,
  updateBillingProfile as updateBillingPlatform,
} from 'api/BillingController';
import { formatCurrency } from 'utils';
import useCanManageBilling from '../../components/ManagePaymentCard/hooks/useCanManageBilling';
import { ViewingLevel } from '.';

// ============================================================================
// Types
// ============================================================================

interface SubscriptionsPageContextShape {
  /** A unique identifier for a paying customer in BillingPlatform. */
  billingCustomerNumber: string | undefined;
  /** Whether an individual user or a brokerage is viewing this page. */
  viewingLevel: ViewingLevel;
  /** The billing profile for the user or brokerage. */
  billingProfile: IKeyholderDataWeb | IBillingProfileDataWeb;
  /** Setter function for updating the billing profile. */
  setBillingProfile: React.Dispatch<
    React.SetStateAction<IKeyholderDataWeb | IBillingProfileDataWeb>
  >;
  /** Whether the user has a saved credit card on file. */
  hasSavedCard: boolean;
  /** Whether we are still fetching the billing profile details. */
  isFetchingProfile: boolean;
  /** Whether we are updating the billing profile details in the db. */
  isUpdatingProfile: boolean;
  /** Function to fetch the billing profile. */
  refetchBillingProfile: () => Promise<void>;
  /** Function to update the billing profile for the user or brokerage. */
  updateBillingProfile: (
    updatedBillingProfile: IKeyholderDataWeb | IBillingProfileDataWeb,
    resetPaymentStatus?: boolean,
  ) => Promise<void>;
  /** The error encountered while updating the billing profile details. */
  updateProfileError: Error | undefined;
  /** Whether the user/brokerage has an overdue balance that is still unpaid. */
  isSuspended: boolean;
  /** The total overdue amount owed by this customer, if they have one. */
  overdueBalance: string | null;
  /** The date on which the customer's subscriptions will be suspended (cancelled) if they have not paid, if they have one. */
  suspensionDate: Date | null;
  /** Whether the user/brokerage has an overdue balance that is still unpaid. */
  hasOverdueBalance: boolean;
  /** Indicates whether the user/brokerage can perform billing related actions. */
  canManageBilling: CanManageBillingProfileType;
  /** Next payment due date */
  nextPaymentDueDate: Date | null;
}

interface SubscriptionsPageProviderProps {
  billingCustomerNumber?: string;
  children: React.ReactNode;
  orgId: string;
  viewingLevel: ViewingLevel;
}

/**
 * Indicates (via `response`) whether the user has permission
 * to manage some aspect of the billing profile.
 *
 * If the user does not have permission, the `message` field
 * will contain the reason why.
 */
export type CanManageBillingProfileType =
  | { response: true }
  | { response: false; message: string };

/**
 * Indicates (via `response`) whether the user has an overdue
 * balance that is still unpaid.
 *
 * If the user is overdue by some amount, the `message` field
 * will display the confirmation for the user to agree to pay the
 * amount.
 */
export type OverdueBalanceType = { response: false } | { response: true };

// ============================================================================
// Context
// ============================================================================

const SubscriptionsPageContext =
  createContext<SubscriptionsPageContextShape | null>(null);

/**
 * Hook to consume SubscriptionsPageContext.
 * Throws error if used outside of provider.
 */
export function useSubscriptionsPageContext(): SubscriptionsPageContextShape {
  const context = useContext(SubscriptionsPageContext);
  if (!context) {
    throw new Error(
      'useSubscriptionsPageContext must be used within a SubscriptionsPageProvider.',
    );
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

/**
 * Provides shared billing profile data to all components on the Subscriptions & Payments page.
 * Fetches billing profile once and shares it with child components.
 */
export const SubscriptionsPageProvider: React.FC<
  SubscriptionsPageProviderProps
> = ({ billingCustomerNumber, children, orgId, viewingLevel }) => {
  const {
    fetchAuth,
    fetchSupraSubscription,
    auth,
    brokerageSubscriptionsBillingDetails,
    userSubscriptionsBillingDetails,
  } = useContext(AuthContext);

  const [billingProfile, setBillingProfile] = useState<
    IKeyholderDataWeb | IBillingProfileDataWeb | null
  >();

  const [updateProfileError, setUpdateProfileError] = useState<Error>();
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  /** The total overdue amount owed by this customer */
  const [overdueBalance, setOverdueBalance] = useState<string | null>(null);
  /** The date on which the customer's subscriptions will be suspended (cancelled) if they have not paid. */
  const [suspensionDate, setSuspensionDate] = useState<Date | null>(null);
  /** The date of the customer's next payment due */
  const [nextPaymentDueDate, setNextPaymentDueDate] = useState<Date | null>(
    null,
  );

  const canManageBilling = useCanManageBilling();

  /**
   * Tracks whether this provider is still mounted.
   */
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Whether the current user is allowed to view the requested viewing level.
   */
  const isAuthorizedForViewingLevel =
    viewingLevel === 'user' || auth.roles[auth.role] >= auth.roles.TEAM_ADMIN;

  const fetchBillingProfile = useCallback(async () => {
    // Don't fetch data the user isn't authorized to see. AuthRedirects will
    // redirect them.
    if (!isAuthorizedForViewingLevel) return;

    if (!billingCustomerNumber || !orgId) {
      setBillingProfile(null);
      return;
    }

    setBillingProfile(null); // Clear previous profile while fetching new one
    setIsFetchingProfile(true);

    try {
      let profile: IKeyholderProfileData | IBillingProfileData;
      viewingLevel === 'user'
        ? (profile = await getKeyholderBillingProfile({
            orgId,
            billingCustomerNumber,
          }))
        : (profile = await getBillingProfile({
            billingCustomerNumber,
          }));
      // The provider may have unmounted while the request was in flight
      if (!isMountedRef.current) return;
      setBillingProfile(profile);
    } catch (error) {
      if (!isMountedRef.current) return;
      const err =
        error instanceof Error
          ? error
          : new Error(
              `Failed to fetch billing profile for billingCustomerNumber: ${
                billingCustomerNumber
              }`,
            );
      message.error('Failed to load billing information.');
    } finally {
      if (isMountedRef.current) {
        setIsFetchingProfile(false);
      }
    }
  }, [viewingLevel, billingCustomerNumber, orgId, isAuthorizedForViewingLevel]);

  useEffect(() => {
    fetchBillingProfile();
  }, [fetchBillingProfile]);

  const hasSavedCard = useMemo(
    () => Boolean(billingProfile?.CCNumber && billingProfile?.CardholderName),
    [billingProfile?.CCNumber, billingProfile?.CardholderName],
  );

  const updateBillingProfile = async (
    updatedBillingProfile: IKeyholderDataWeb | IBillingProfileDataWeb,
    resetPaymentStatus?: boolean,
  ) => {
    if (!updatedBillingProfile) {
      setUpdateProfileError(new Error('No billing profile provided'));
      return;
    }

    setIsUpdatingProfile(true);
    try {
      // TODO end goal is to combine these two "update" functions into one call to the SupraBillingAPI
      let profile: IKeyholderDataWeb | IBillingProfileDataWeb;
      viewingLevel === 'user'
        ? (profile = await updateKeyholderBillingProfile({
            billingProfile: updatedBillingProfile as IKeyholderDataWeb,
            resetPaymentStatus: resetPaymentStatus ?? false,
          }))
        : (profile = await updateBillingPlatform({
            billingProfile: updatedBillingProfile as IBillingProfileDataWeb,
          }));

      setBillingProfile(profile);
    } catch (error) {
      // Set error state based on the error returned from the API
      if ('OperationMessage' in error) {
        setUpdateProfileError(new Error(error.OperationMessage));
      } else if (error instanceof Error) {
        setUpdateProfileError(error);
      } else {
        setUpdateProfileError(
          new Error(
            'Unable to update billing profile. Please try again later.',
          ),
        );
      }
      // Re-throw the error to be caught by the caller
      throw error;
    } finally {
      // Update state
      await fetchAuth();
      await fetchSupraSubscription();
      setUpdateProfileError(null);
      setIsUpdatingProfile(false);

      // If no error optimistically assume balance is paid
      setOverdueBalance(null);
      setSuspensionDate(null);
      setIsSuspended(false);
    }
  };

  useEffect(() => {
    /**
     * Determines the next billing date based on the billing anniversary date.
     * Billing happens on the same day each month.
     * NOTE - This assumes MONTHLY billing cycles.
     */
    const determineNextBillingDate = (billingDate?: string) => {
      if (!billingDate) return null;

      const now = moment().startOf('day');
      const nowDay = now.date();

      const anniversary = moment.utc(billingDate).startOf('day');
      const anniversaryDay = anniversary.date();

      if (anniversaryDay < nowDay) {
        now.add(1, 'month');
      }
      now.set({ date: anniversaryDay });

      return now.toDate();
    };

    // Populate the modal with brokerage billing details, if viewing as brokerage admin
    if (viewingLevel === 'org' && brokerageSubscriptionsBillingDetails) {
      const {
        outstandingBalance: returnedBrokerageBalance,
        subscriptionsSuspensionDate,
        billingAnniversaryDate,
      } = brokerageSubscriptionsBillingDetails;

      if (returnedBrokerageBalance && returnedBrokerageBalance > 0) {
        // Set the balance to a nicely-formatted string of the number
        setOverdueBalance(
          formatCurrency(
            returnedBrokerageBalance,
            // Only pass in currencyCode if it's finished loading from the backend.
            brokerageSubscriptionsBillingDetails.currencyCode
              ? {
                  currency: brokerageSubscriptionsBillingDetails.currencyCode,
                  currencyDisplay: 'narrowSymbol',
                  // Show cents, even when they are zero
                  minimumFractionDigits: 2,
                }
              : {},
          ),
        );
      }
      // Set the date the brokerage's subscriptions will be suspended
      setSuspensionDate(subscriptionsSuspensionDate || null);
      setNextPaymentDueDate(determineNextBillingDate(billingAnniversaryDate));
      setIsSuspended(!auth.org.supraPaid);
    }
    // Populate the modal with user billing details, if viewing as individual user
    else if (userSubscriptionsBillingDetails) {
      const {
        outstandingBalance: returnedUserBalance,
        subscriptionsSuspensionDate,
        billingAnniversaryDate,
      } = userSubscriptionsBillingDetails;
      if (returnedUserBalance && returnedUserBalance > 0) {
        // Set the balance to a nicely-formatted string of the number
        setOverdueBalance(
          formatCurrency(
            returnedUserBalance,
            // Only pass in currencyCode if it's finished loading from the backend.
            userSubscriptionsBillingDetails.currencyCode
              ? {
                  currency: userSubscriptionsBillingDetails.currencyCode,
                  currencyDisplay: 'narrowSymbol',
                  // Show cents, even when they are zero
                  minimumFractionDigits: 2,
                }
              : {},
          ),
        );
      }
      // Set the date the user's subscriptions will be suspended
      setSuspensionDate(subscriptionsSuspensionDate || null);
      setNextPaymentDueDate(determineNextBillingDate(billingAnniversaryDate));
      setIsSuspended(!auth.supraPaid);
    }
  }, [
    auth.isInBillingCollection,
    auth.org.isInBillingCollection,
    auth.org.supraPaid,
    auth.supraPaid,
    brokerageSubscriptionsBillingDetails,
    userSubscriptionsBillingDetails,
    viewingLevel,
  ]);

  return (
    <SubscriptionsPageContext.Provider
      value={{
        billingCustomerNumber,
        viewingLevel,
        nextPaymentDueDate,
        billingProfile,
        setBillingProfile,
        hasSavedCard,
        isFetchingProfile,
        isUpdatingProfile,
        refetchBillingProfile: fetchBillingProfile,
        updateBillingProfile,
        canManageBilling,
        updateProfileError,
        isSuspended,
        overdueBalance,
        suspensionDate,
        hasOverdueBalance: Boolean(overdueBalance),
      }}
    >
      {children}
    </SubscriptionsPageContext.Provider>
  );
};

export default SubscriptionsPageContext;
